import L from 'leaflet'
import { reactive } from 'vue'
import { MapDoodleType } from '/@/constants/map'
import { getRoot } from '/@/root'
import { MapDoodleEnum } from '/@/types/map-enum'
import { EFlightAreaType } from '../types/flight-area'
import { message } from 'ant-design-vue'
import pin2d8cf0 from '../assets/icons/pin-2d8cf0.svg'

export function useMouseTool () {
  const root = getRoot()

  const state = reactive({
    pinNum: 0,
    polylineNum: 0,
    PolygonNum: 0,
    currentType: '',
  })

  const flightAreaColorMap: Record<EFlightAreaType, string> = {
    [EFlightAreaType.DFENCE]: '#19be6b',
    [EFlightAreaType.NFZ]: '#ff0000',
  }

  // 현재 활성화된 draw:created 핸들러
  let activeDrawHandler: ((e: any) => void) | null = null
  let activeDrawControl: any = null

  function getMap (): L.Map {
    return root.$map as L.Map
  }

  // $aMap에는 L(leaflet) 전체가 저장되어 있음 (use-g-map.ts 참고)
  function getL (): any {
    return root.$aMap
  }

  function startDraw (drawControl: any, getDrawCallback: Function, extData?: any) {
    const map = getMap()
    if (!map) return

    stopDraw() // 이전 핸들러 제거

    activeDrawControl = drawControl
    drawControl.enable()

    activeDrawHandler = (e: any) => {
      const layer = e.layer
      if (extData) {
        ;(layer as any)._extData = extData
      }
      // GMap.vue 호환: { obj: layer } 형태로 전달
      getDrawCallback({ obj: layer })
      stopDraw()
    }
    map.on('draw:created', activeDrawHandler)
  }

  function stopDraw () {
    const map = getMap()
    if (map && activeDrawHandler) {
      map.off('draw:created', activeDrawHandler)
      activeDrawHandler = null
    }
    if (activeDrawControl) {
      try { activeDrawControl.disable() } catch (_) {}
      activeDrawControl = null
    }
  }

  function drawPin (_type: MapDoodleType, getDrawCallback: Function) {
    const map = getMap()
    const Ldraw = getL()
    if (!map || !Ldraw?.Draw?.Marker) return
    const handler = new (L as any).Draw.Marker(map, {
      icon: L.icon({
        iconUrl: pin2d8cf0,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
      })
    })
    startDraw(handler, getDrawCallback)
  }

  function drawPolyline (_type: MapDoodleType, getDrawCallback: Function) {
    const map = getMap()
    const Ldraw = getL()
    if (!map || !Ldraw?.Draw?.Polyline) return
    const handler = new Ldraw.Draw.Polyline(map, {
      shapeOptions: { color: '#2d8cf0', weight: 2, opacity: 1 }
    })
    startDraw(handler, getDrawCallback)
  }

  function drawPolygon (_type: MapDoodleType, getDrawCallback: Function) {
    const map = getMap()
    const Ldraw = getL()
    if (!map || !Ldraw?.Draw?.Polygon) return
    const handler = new Ldraw.Draw.Polygon(map, {
      shapeOptions: { color: '#2d8cf0', weight: 2, fillColor: '#1791fc', fillOpacity: 0.4 }
    })
    startDraw(handler, getDrawCallback)
  }

  function drawFlightAreaPolygon (type: EFlightAreaType, getDrawCallback: Function) {
    const map = getMap()
    const Ldraw = getL()
    if (!map || !Ldraw?.Draw?.Polygon) return
    const handler = new Ldraw.Draw.Polygon(map, {
      shapeOptions: {
        color: flightAreaColorMap[type],
        weight: 4,
        fillColor: flightAreaColorMap[type],
        fillOpacity: EFlightAreaType.NFZ === type ? 0.3 : 0,
        dashArray: '10, 5',
      }
    })
    startDraw(handler, getDrawCallback, { type, mapType: 'polygon' })
  }

  function drawFlightAreaCircle (type: EFlightAreaType, getDrawCallback: Function) {
    const map = getMap()
    const Ldraw = getL()
    if (!map || !Ldraw?.Draw?.Circle) return
    const handler = new Ldraw.Draw.Circle(map, {
      shapeOptions: {
        color: flightAreaColorMap[type],
        weight: 6,
        fillColor: flightAreaColorMap[type],
        fillOpacity: EFlightAreaType.NFZ === type ? 0.3 : 0,
        dashArray: '10, 5',
      }
    })
    startDraw(handler, getDrawCallback, { type, mapType: 'circle' })
  }

  function mouseTool (type: MapDoodleType, getDrawCallback: Function, flightAreaType?: EFlightAreaType) {
    state.currentType = type
    if (flightAreaType) {
      switch (type) {
        case MapDoodleEnum.POLYGON:
          drawFlightAreaPolygon(flightAreaType, getDrawCallback)
          return
        case MapDoodleEnum.CIRCLE:
          drawFlightAreaCircle(flightAreaType, getDrawCallback)
          return
        default:
          message.error(`Invalid type: ${flightAreaType}`)
          return
      }
    }
    switch (type) {
      case MapDoodleEnum.PIN:
        drawPin(type, getDrawCallback)
        break
      case MapDoodleEnum.POLYLINE:
        drawPolyline(type, getDrawCallback)
        break
      case MapDoodleEnum.POLYGON:
        drawPolygon(type, getDrawCallback)
        break
      case MapDoodleEnum.Close:
        stopDraw()
        break
    }
  }

  return {
    mouseTool
  }
}
